import type { LintDiagnostic } from '../../types/lint.js';
import type { CharterFrontmatter } from '../../types/charter.js';
import type { ToolsFrontmatter } from '../../types/tools.js';

/**
 * Cross-file consistency checks between CHARTER.md and TOOLS.md.
 */
export function runCrossFileRules(charter: CharterFrontmatter, tools: ToolsFrontmatter): LintDiagnostic[] {
  const diagnostics: LintDiagnostic[] = [];

  // agent_id must match
  if (charter.agent_id !== tools.agent_id) {
    diagnostics.push({
      file: 'CHARTER.md + TOOLS.md',
      code: 'CROSS.AGENT_ID_MISMATCH',
      severity: 'error',
      message: `CHARTER.md agent_id "${charter.agent_id}" does not match TOOLS.md agent_id "${tools.agent_id}"`,
    });
  }

  // code_name must match
  if (charter.code_name !== tools.code_name) {
    diagnostics.push({
      file: 'CHARTER.md + TOOLS.md',
      code: 'CROSS.CODE_NAME_MISMATCH',
      severity: 'error',
      message: `CHARTER.md code_name "${charter.code_name}" does not match TOOLS.md code_name "${tools.code_name}"`,
    });
  }

  // environment must match
  if (charter.environment !== tools.environment) {
    diagnostics.push({
      file: 'CHARTER.md + TOOLS.md',
      code: 'CROSS.ENVIRONMENT_MISMATCH',
      severity: 'error',
      message: `CHARTER.md environment "${charter.environment}" does not match TOOLS.md environment "${tools.environment}"`,
    });
  }

  // logging_mode should match logging_redaction
  if (charter.logging_mode !== tools.global_controls.logging_redaction) {
    diagnostics.push({
      file: 'CHARTER.md + TOOLS.md',
      code: 'CROSS.LOGGING_MISMATCH',
      path: 'logging_mode / global_controls.logging_redaction',
      severity: 'warning',
      message: `CHARTER.md logging_mode "${charter.logging_mode}" does not match TOOLS.md logging_redaction "${tools.global_controls.logging_redaction}"`,
    });
  }

  // version should match
  if (charter.version !== tools.version) {
    diagnostics.push({
      file: 'CHARTER.md + TOOLS.md',
      code: 'CROSS.VERSION_MISMATCH',
      severity: 'warning',
      message: `CHARTER.md version "${charter.version}" does not match TOOLS.md version "${tools.version}"`,
    });
  }

  // TOOLS.PUBLISH.PUBLIC_EXPOSURE (ADR 0010 §Public-exposure governance).
  //
  // An Augmented Live (agt-live) publish on a prod or High-risk-tier agent is
  // the highest-exposure combination: permanent + public output under an
  // authoritative identity. The warning forces a human acknowledgement on
  // grant. Mirrors CHARTER.CHANNELS.PII_ON_LIMITED — not an error (operators
  // with a real need can ack and proceed), but loud enough to be impossible to
  // miss in review.
  //
  // The signal is the *tool grant* in TOOLS.md, not metadata on the
  // integration — that's what an operator edits when they hand an agent a
  // capability. The rule is intentionally tight so an unrelated tool doesn't
  // false-positive.
  if (charter.environment === 'prod' || charter.risk_tier === 'High') {
    for (let i = 0; i < tools.tools.length; i++) {
      const tool = tools.tools[i]!;
      if (isAgtLivePublishTool(tool.id)) {
        diagnostics.push({
          file: 'CHARTER.md + TOOLS.md',
          code: 'TOOLS.PUBLISH.PUBLIC_EXPOSURE',
          path: `tools[${i}].id`,
          severity: 'warning',
          message:
            `Tool "${tool.id}" grants Augmented Live publishing (permanent, public) to a ` +
            `${charter.environment === 'prod' ? 'production' : 'High-risk-tier'} agent. ` +
            `Confirm the public-exposure surface is intended before granting it.`,
        });
      }
    }
  }

  return diagnostics;
}

function isAgtLivePublishTool(id: string): boolean {
  // TOOLS.md tool ids are constrained to kebab-case by the schema
  // (`^[a-z0-9]+(-[a-z0-9]+)*$`) — no dots or underscores allowed. Match the
  // canonical agt-live publish grant; anchor on the `agt-live-` prefix so an
  // unrelated tool doesn't false-positive. Augmented Live sites are always
  // permanent + public (no anonymous/TTL variant), so any publish grant trips it.
  return /^agt-live-publish(-account)?$/.test(id);
}
